import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../services/inventoryApi';
import searchService from '../../services/searchService';
import { getRoleDisplayNameLocalized, getUserInitial } from '../../utils/userDisplay';
const AdminSidebar = ({ isOpen, onClose, isCollapsed, onToggleCollapse, isSearchActive, hasImpersonationBanner, shellConfig = null }) => {
  const { isDark } = useTheme();
  const { hasPermission, user, logout } = useAdminAuth();
  const { t } = useTranslation('admin');
  const effectiveHasPermission = shellConfig?.hasPermission || hasPermission;
  const effectiveUser = shellConfig?.user || user;
  const effectiveLogout = shellConfig?.logout || logout;

  // Add custom scrollbar styling for webkit browsers
  useEffect(() => {
    const styleId = 'sidebar-scrollbar-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .admin-sidebar-nav::-webkit-scrollbar {
          width: 6px;
        }
        .admin-sidebar-nav::-webkit-scrollbar-track {
          background: transparent;
        }
        .admin-sidebar-nav::-webkit-scrollbar-thumb {
          background: rgba(201, 160, 56, 0.3);
          border-radius: 3px;
        }
        .admin-sidebar-nav::-webkit-scrollbar-thumb:hover {
          background: rgba(201, 160, 56, 0.5);
        }
        .theme-dark .admin-sidebar-nav::-webkit-scrollbar-thumb {
          background: rgba(201, 160, 56, 0.4);
        }
        .theme-dark .admin-sidebar-nav::-webkit-scrollbar-thumb:hover {
          background: rgba(201, 160, 56, 0.6);
        }
      `;
      document.head.appendChild(style);
    }
  }, []);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [aiDropdownOpen, setAiDropdownOpen] = useState(false);
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [mobileSearchResults, setMobileSearchResults] = useState([]);
  const [mobileSearching, setMobileSearching] = useState(false);
  const [showMobileSearchResults, setShowMobileSearchResults] = useState(false);
  const mobileSearchTimeoutRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const mobileSearchRef = useRef(null);

  // Load low stock count for inventory badge
  useEffect(() => {
    const loadLowStockCount = async () => {
      if (shellConfig?.disableInventoryBadge) {
        setLowStockCount(0);
        return;
      }
      if (effectiveHasPermission('inventory')) {
        try {
          const response = await inventoryApi.alerts.getLowStock({
            skipActivityTracking: true
          });
          setLowStockCount(response.items?.length || 0);
        } catch (error) {
          console.error('Error loading low stock count:', error);
          setLowStockCount(0);
        }
      }
    };

    loadLowStockCount();
    
    // Refresh every 5 minutes
    const interval = setInterval(loadLowStockCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [effectiveHasPermission, shellConfig?.disableInventoryBadge]);

  // Define AI dropdown items
  const aiDropdownItems = shellConfig?.dropdownItems || [
    { path: '/admin/haircuts-studio', label: t('admin.nav_haircuts_studio'), icon: 'scissors', permission: 'capture' },
    { path: '/admin/makeup-studio', label: t('admin.nav_makeup_studio'), icon: 'lipstick', permission: 'capture' },
    // { path: '/admin/botox-filler-studio', label: 'Botox & Filler Studio', icon: 'syringe', permission: 'capture' }, // HIDDEN: Botox & Filler feature disabled
    { path: '/admin/nails-studio', label: t('admin.nav_nails_studio'), icon: 'hand-nails', permission: 'capture' },
    { path: '/admin/ai-analysis', label: t('admin.nav_ai_analysis'), icon: 'ai-scan', permission: 'ai-analysis' },
    { path: '/admin/suggestions', label: t('admin.nav_hairstyle_suggestions'), icon: 'lightbulb', permission: 'suggestions' },
  ];

  // Handle dropdown close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setAiDropdownOpen(false);
      }
    };

    if (aiDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [aiDropdownOpen]);

  // Check if any AI page is currently active
  const isAiPageActive = () => {
    return aiDropdownItems.some(item => location.pathname === item.path);
  };

  // Auto-close dropdown when sidebar is collapsed
  useEffect(() => {
    if (isCollapsed && aiDropdownOpen) {
      setAiDropdownOpen(false);
    }
  }, [isCollapsed, aiDropdownOpen]);

  // Mobile global search (debounced)
  const handleMobileSearchChange = (e) => {
    const q = e.target.value;
    setMobileSearchQuery(q);

    if (mobileSearchTimeoutRef.current) {
      clearTimeout(mobileSearchTimeoutRef.current);
    }

    if (q.trim().length >= 2) {
      setMobileSearching(true);
      setShowMobileSearchResults(true);
      mobileSearchTimeoutRef.current = setTimeout(async () => {
        try {
          const results = shellConfig?.globalSearch
            ? await shellConfig.globalSearch(q.trim())
            : await searchService.globalSearch(q.trim(), {
                includeBookings: effectiveHasPermission('bookings'),
                includeClients: effectiveHasPermission('clients'),
                includeBarbers: effectiveHasPermission('barbers'),
                includeServices: effectiveHasPermission('services'),
                includeInventory: effectiveHasPermission('inventory'),
                includeAiTools: effectiveHasPermission('ai-analysis') || effectiveHasPermission('clients'),
              });
          setMobileSearchResults(results || []);
        } catch (err) {
          console.error('Mobile search error:', err);
          setMobileSearchResults([]);
        } finally {
          setMobileSearching(false);
        }
      }, 250);
    } else {
      setMobileSearchResults([]);
      setMobileSearching(false);
      setShowMobileSearchResults(false);
    }
  };

  const handleMobileSearchSubmit = (e) => {
    e.preventDefault();
    const q = mobileSearchQuery.trim();
    if (q.length >= 2) {
      onClose();
      if (shellConfig?.handleSearchSubmit) {
        shellConfig.handleSearchSubmit(q, navigate);
      } else if (effectiveHasPermission('bookings')) {
        navigate(`/admin/bookings?q=${encodeURIComponent(q)}`);
      } else if (effectiveHasPermission('clients')) {
        navigate(`/admin/clients?q=${encodeURIComponent(q)}`);
      } else if (effectiveHasPermission('barbers')) {
        navigate(`/admin/barbers?q=${encodeURIComponent(q)}`);
      }
      setShowMobileSearchResults(false);
      setMobileSearchResults([]);
      setMobileSearchQuery('');
    }
  };

  const handleMobileSearchResultClick = (result) => {
    onClose();
    navigate(result.url);
    setShowMobileSearchResults(false);
    setMobileSearchResults([]);
    setMobileSearchQuery('');
  };

  // Close mobile search results when clicking outside
  useEffect(() => {
    const onDown = (event) => {
      if (mobileSearchRef.current && !mobileSearchRef.current.contains(event.target)) {
        setShowMobileSearchResults(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (mobileSearchTimeoutRef.current) clearTimeout(mobileSearchTimeoutRef.current);
    };
  }, []);

  const getUserDisplayName = () => effectiveUser?.name || effectiveUser?.email || t('admin.user_fallback');
  const getRoleDisplayName = () => getRoleDisplayNameLocalized(effectiveUser?.role, t);

  const navItems = shellConfig?.navItems || [
    { path: '/admin', label: t('admin.nav_dashboard'), icon: 'dashboard', permission: 'dashboard' },
    { 
      type: 'dropdown', 
      label: t('admin.nav_ai_tools'), 
      icon: 'ai-sparkles', 
      items: aiDropdownItems,
      permission: 'capture' // Base permission - will filter individual items separately
    },
    { path: '/admin/bookings', label: t('admin.nav_bookings'), icon: 'calendar', permission: 'bookings' },
    { path: '/admin/clients', label: t('admin.nav_clients'), icon: 'users', permission: 'clients' },
    { path: '/admin/barbers', label: t('admin.nav_barbers'), icon: 'user-check', permission: 'barbers' },
    { path: '/admin/services', label: t('admin.nav_services'), icon: 'scissors', permission: 'services' },
    { path: '/admin/inventory', label: t('admin.nav_inventory'), icon: 'boxes', permission: 'inventory' },
    { path: '/admin/availability', label: t('admin.nav_availability'), icon: 'clock', permission: 'availability' },
    { path: '/admin/qr', label: t('admin.nav_qr_generator'), icon: 'qr-code', permission: 'settings' },
    { path: '/admin/settings', label: t('admin.nav_settings'), icon: 'settings', permission: 'settings' },
  ];

  // Filter nav items based on user permissions
  const filteredNavItems = navItems.filter(item => {
    if (item.type === 'dropdown') {
      // For dropdown items, check if user has permission for at least one sub-item
      return item.items.some(subItem => effectiveHasPermission(subItem.permission));
    }
    return effectiveHasPermission(item.permission);
  });

  // Icon component to render SVG icons
  const IconComponent = ({ iconName, isActive = false }) => {
    const iconProps = {
      className: `h-5 w-5 shrink-0`,
      fill: "none",
      stroke: "currentColor",
      viewBox: "0 0 24 24",
        style: {
          color: isActive 
            ? (isDark ? '#f0d9a6' : 'var(--gold-600)')
            : (isDark ? '#d2d6db' : '#4a4a4a')
        }
    };

    switch (iconName) {
      case 'dashboard':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      case 'camera':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
      case 'lightbulb':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        );
      case 'magic-wand':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8l2 2" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 4l1 1m-1-1l-1-1m1 1l-1 1m1-1l1-1" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 10l1 1m-1-1l-1-1m1 1l-1 1m1-1l1-1" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l1 1m-1-1l-1-1m1 1l-1 1m1-1l1-1" />
          </svg>
        );
      case 'brain':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        );
      case 'calendar':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case 'users':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        );
      case 'user-check':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
          </svg>
        );
      case 'scissors':
        return (
          <svg {...iconProps}>
            <circle cx="6" cy="6" r="2" strokeWidth={2} />
            <circle cx="6" cy="18" r="2" strokeWidth={2} />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7l12 10M8 17l12-10" />
          </svg>
        );
      case 'boxes':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        );
      case 'clock':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'qr-code':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 16a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1v-4zM13 4a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V4zM13 16a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4zM6 6h2v2H6V6zM6 18h2v2H6v-2zM18 6h2v2h-2V6zM18 18h2v2h-2v-2zM9 9h2v2H9V9zM9 15h2v2H9v-2zM15 9h2v2h-2V9zM15 15h2v2h-2v-2zM12 12h2v2h-2v-2z" />
          </svg>
        );
      case 'settings':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
      case 'user':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      case 'sparkles':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        );
      case 'hand-nails':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 13V8a1 1 0 012 0v5m0 0V7a1 1 0 112 0v6m0 0V7a1 1 0 112 0v7m0 0V9a1 1 0 112 0v8a4 4 0 01-4 4H9a4 4 0 01-4-4v-1a3 3 0 013-3h1z" />
            <circle cx="9" cy="6.5" r="0.8" fill="currentColor" strokeWidth={2} />
            <circle cx="11" cy="5.5" r="0.8" fill="currentColor" strokeWidth={2} />
            <circle cx="13" cy="5.5" r="0.8" fill="currentColor" strokeWidth={2} />
            <circle cx="15" cy="7.5" r="0.8" fill="currentColor" strokeWidth={2} />
          </svg>
        );
      case 'nail-polish':
        return (
          <svg {...iconProps}>
            <rect x="9" y="14" width="6" height="7" rx="1" strokeWidth={2} />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14V9a2 2 0 014 0v5" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 9V7a1 1 0 012 0v2" />
            <line x1="10.5" y1="17" x2="13.5" y2="17" strokeLinecap="round" strokeWidth={2} />
          </svg>
        );
      case 'hand':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11V6a1 1 0 012 0v5m0 0V5a1 1 0 112 0v6m0 0V5a1 1 0 112 0v7m0 0V7a1 1 0 112 0v9a4 4 0 01-4 4H8a4 4 0 01-4-4v-1a3 3 0 013-3h1z" />
          </svg>
        );
      case 'ai-sparkles':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7l1.5 1.5L7 10M17 7l-1.5 1.5L17 10" />
          </svg>
        );
      case 'chevron-down':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        );
      case 'chevron-left':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        );
      case 'chevron-right':
        return (
          <svg {...iconProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        );
      case 'lipstick':
        return (
          <svg {...iconProps}>
            <rect x="9" y="12" width="6" height="9" rx="1" strokeWidth={2} />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 12V8a2 2 0 014 0v4" />
            <ellipse cx="12" cy="8" rx="2.5" ry="1.5" strokeWidth={2} />
            <line x1="9" y1="16" x2="15" y2="16" strokeLinecap="round" strokeWidth={2} />
            <line x1="9" y1="18" x2="15" y2="18" strokeLinecap="round" strokeWidth={2} />
          </svg>
        );
      case 'makeup-brush':
        return (
          <svg {...iconProps}>
            <ellipse cx="8" cy="16" rx="2.5" ry="3" transform="rotate(-25 8 16)" strokeWidth={2} />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.5 14.5l8-8M17.5 6.5l2-2a1.5 1.5 0 012 0 1.5 1.5 0 010 2l-2 2" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l7.5-7.5" strokeWidth={1.5} />
          </svg>
        );
      case 'palette':
        return (
          <svg {...iconProps}>
            <circle cx="12" cy="12" r="8" strokeWidth={2} />
            <circle cx="8.5" cy="9" r="1" strokeWidth={2} />
            <circle cx="11" cy="7" r="1" strokeWidth={2} />
            <circle cx="7" cy="12.5" r="1" strokeWidth={2} />
            <circle cx="10" cy="15" r="1" strokeWidth={2} />
            <circle cx="16" cy="13" r="2" strokeWidth={2} />
          </svg>
        );
      case 'syringe':
        return (
          <svg {...iconProps}>
            <rect x="4" y="10" width="10" height="4" rx="1" strokeWidth={2} />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 12h6M4 12H2M2 10v4M6 8v2M9 8v2M12 8v2" />
          </svg>
        );
      case 'ai-scan':
        return (
          <svg {...iconProps}>
            <circle cx="12" cy="12" r="3" strokeWidth={2} />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2v2M12 20v2M2 12h2M20 12h2" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
            <circle cx="12" cy="12" r="7" strokeDasharray="2 3" opacity="0.5" strokeWidth={2} />
            <circle cx="12" cy="12" r="9.5" strokeDasharray="3 4" opacity="0.3" strokeWidth={2} />
          </svg>
        );
      case 'cpu-chip':
        return (
          <svg {...iconProps}>
            <rect x="7" y="7" width="10" height="10" rx="2" strokeWidth={2} />
            <rect x="10" y="10" width="4" height="4" rx="1" strokeWidth={2} />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 1v4M15 1v4M9 19v4M15 19v4M1 9h4M1 15h4M19 9h4M19 15h4" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Overlay for mobile + tablet */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 min-[1025px]:hidden"
          onClick={(e) => {
            onClose();
          }}
        />
      )}

      {/* Sidebar - Hidden on tablets, shown only on desktop */}
      <div className={`fixed ${hasImpersonationBanner ? 'top-32' : 'top-24'} left-0 z-50 transition-all duration-300 ease-in-out w-[78vw] max-w-[320px] ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } min-[1025px]:translate-x-0 ${
        isSearchActive ? 'min-[1025px]:blur-sm min-[1025px]:opacity-70' : ''
      } ${
        isCollapsed ? 'min-[1025px]:w-16' : 'min-[1025px]:w-64'
      } admin-sidebar ${isDark ? 'theme-dark' : 'theme-light'}`}
      style={{
        height: hasImpersonationBanner ? 'calc(100vh - 128px)' : 'calc(100vh - 96px)',
      }}>
        
        {/* Content Container */}
        <div className={`relative z-10 w-full h-full transition-all duration-300 ease-in-out flex flex-col ${
          isCollapsed ? 'md:w-16' : 'md:w-64'
        }`} style={{ color: isDark ? 'var(--text)' : 'var(--text)' }}>
          
          {/* Panel Header - Collapse/Expand toggle only */}
          <div className={`p-4 border-b transition-all duration-300 ${
            isCollapsed ? 'px-2' : 'px-4'
          }`} style={{ borderColor: isDark ? 'var(--panel-border)' : 'var(--panel-border)' }}>
            <div className="flex items-center justify-end">
              {/* Collapse/Expand toggle - desktop only */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleCollapse();
                }}
                className={`hidden min-[1025px]:inline-flex items-center justify-center rounded-full p-1.5 clickable-item ${isDark ? 'theme-dark' : 'theme-light'}`}
                style={{
                  border: '1px solid #c9a038',
                  background: isDark ? '#2a2f36' : '#ffffff',
                  color: isDark ? '#f0d9a6' : '#7d6633'
                }}
                aria-label={isCollapsed ? t('admin.expand_sidebar') : t('admin.collapse_sidebar')}
                title={isCollapsed ? t('admin.expand') : t('admin.collapse')}
              >
                <IconComponent iconName={isCollapsed ? 'chevron-right' : 'chevron-left'} isActive={false} />
              </button>
            </div>
          </div>

          {/* Mobile + Tablet: Profile header + Global Search (moved from Topbar) */}
          <div className="p-4 min-[1025px]:hidden space-y-3">
            {/* Profile / initial */}
            <button
              onClick={() => {
                onClose();
                navigate('/admin/profile');
              }}
              className="w-full flex items-center gap-3 rounded-xl p-3 clickable-item"
              style={{
                background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
              }}
              aria-label={t('admin.open_profile')}
            >
              <div className="h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold" style={{ backgroundColor: '#7d6633' }}>
                {getUserInitial(effectiveUser)}
              </div>
              <div className="min-w-0 text-left">
                <div className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {getUserDisplayName()}
                </div>
                <div className={`text-xs truncate ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                  {getRoleDisplayName()}
                </div>
              </div>
            </button>

            {/* Global Search */}
            <div ref={mobileSearchRef} className="relative">
              <form onSubmit={handleMobileSearchSubmit} className="relative">
                <input
                  type="text"
                  placeholder={shellConfig?.mobileSearchPlaceholder || t('admin.mobile_search_placeholder')}
                  value={mobileSearchQuery}
                  onChange={handleMobileSearchChange}
                  onFocus={() => {
                    if (mobileSearchQuery.trim().length >= 2) setShowMobileSearchResults(true);
                  }}
                  className={`w-full pl-10 pr-4 py-2.5 rounded-xl admin-search clickable-item ${isDark ? 'theme-dark' : 'theme-light'} min-w-0`}
                  style={{
                    background: isDark ? '#2a2f36' : '#f9f9f9',
                    border: isDark ? '1px solid #383e45' : '1px solid #dddddd',
                    color: isDark ? '#e7ebef' : 'var(--text)',
                    fontSize: '14px',
                  }}
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {mobileSearching ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-transparent border-t-current text-c9a038"></div>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )}
                </div>
              </form>

              {showMobileSearchResults && mobileSearchQuery.trim().length >= 2 && (
                <div
                  className={`mt-2 rounded-xl border shadow-lg overflow-hidden ${
                    isDark ? 'bg-gray-800/95 border-gray-700' : 'bg-white border-gray-200'
                  }`}
                  style={{ maxHeight: 320 }}
                >
                  {mobileSearchResults.length > 0 ? (
                    <div className="max-h-80 overflow-y-auto">
                      {mobileSearchResults.slice(0, 8).map((result, idx) => (
                        <button
                          key={`${result.type}-${result.id}-${idx}`}
                          onClick={() => handleMobileSearchResultClick(result)}
                          className={`w-full px-4 py-3 text-left transition-colors ${
                            isDark ? 'text-white hover:bg-white/10' : 'text-gray-900 hover:bg-gray-100'
                          }`}
                        >
                          <div className="text-sm font-semibold truncate">{result.title}</div>
                          <div className={`text-xs truncate ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{result.subtitle}</div>
                        </button>
                      ))}
                      <button
                        onClick={handleMobileSearchSubmit}
                        className={`w-full px-4 py-2 text-left text-sm font-semibold border-t ${
                          isDark ? 'text-[#c9a038] border-white/10 hover:bg-white/5' : 'text-[#7d6633] border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {t('admin.search_view_all')} "{mobileSearchQuery.trim()}"
                      </button>
                    </div>
                  ) : !mobileSearching ? (
                    <div className={`px-4 py-3 text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                      {t('admin.search_no_results')}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          {/* Navigation - Scrollable */}
          <nav className="flex-1 p-4 pb-6 overflow-y-auto admin-sidebar-nav" style={{
            scrollbarWidth: 'thin',
            scrollbarColor: isDark ? 'rgba(201, 160, 56, 0.4) transparent' : 'rgba(125, 102, 51, 0.3) transparent',
          }}>
            <ul className="space-y-4">
              {filteredNavItems.map((item, index) => (
                <li key={item.path || `dropdown-${index}`}>
                  {item.type === 'dropdown' ? (
                    // Dropdown menu item
                    <div ref={dropdownRef} className="relative">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isCollapsed) {
                            onToggleCollapse(); // Auto-expand when clicking on dropdown in collapsed mode
                          } else {
                            setAiDropdownOpen(!aiDropdownOpen);
                          }
                        }}
                        className={`w-full flex items-center ${
                          isCollapsed ? 'justify-center' : 'justify-between'
                        } admin-nav-item clickable-item ${
                          isAiPageActive() ? 'active nav-item-active-entrance' : ''
                        } ${isDark ? 'theme-dark' : 'theme-light'} ${
                          isCollapsed ? 'p-2 rounded-md' : ''
                        }`}
                        title={isCollapsed ? item.label : ''}
                      >
                        <div className={`flex items-center ${isCollapsed ? '' : 'space-x-3'}`} style={{minWidth: isCollapsed ? 'auto' : 0}}>
                          <IconComponent iconName={item.icon} isActive={isAiPageActive()} />
                          {!isCollapsed && (
                            <span className="font-medium" style={{
                              color: isAiPageActive() 
                                ? (isDark ? '#f0d9a6' : 'var(--gold-600)')
                                : (isDark ? 'var(--text)' : 'var(--text)')
                            }}>{item.label}</span>
                          )}
                        </div>
                        {!isCollapsed && (
                          <div className={`transition-transform duration-200 ${aiDropdownOpen ? 'rotate-180' : ''}`}>
                            <IconComponent 
                              iconName="chevron-down" 
                              isActive={false}
                            />
                          </div>
                        )}
                      </button>

                      {/* Dropdown menu */}
                      {aiDropdownOpen && !isCollapsed && (
                        <div className={`mt-1 transition-all duration-200 ease-out transform origin-top ${
                          aiDropdownOpen ? 'scale-y-100 opacity-100' : 'scale-y-0 opacity-0'
                        }`}>
                          <div 
                            className="ml-0 pl-0 space-y-1 rounded-xl mt-2 py-2"
                            style={{
                              background: isDark 
                                ? 'rgba(42, 47, 54, 0.85)' 
                                : 'rgba(249, 249, 249, 0.92)',
                              backdropFilter: 'blur(12px)',
                              WebkitBackdropFilter: 'blur(12px)',
                              border: isDark 
                                ? '1px solid rgba(201, 160, 56, 0.2)' 
                                : '1px solid rgba(125, 102, 51, 0.15)',
                              boxShadow: isDark 
                                ? '0 4px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(201, 160, 56, 0.1)' 
                                : '0 4px 20px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(125, 102, 51, 0.08)'
                            }}
                          >
                            {item.items
                              .filter(subItem => effectiveHasPermission(subItem.permission))
                              .map((subItem) => (
                              subItem.disabled ? (
                                // TEMPORARILY DISABLED — shows "Coming Soon" badge, no navigation
                                <div
                                  key={subItem.path}
                                  className={`
                                    flex items-center space-x-3 text-sm mx-2 py-2 px-3 rounded-md cursor-not-allowed opacity-50
                                    ${isDark ? 'theme-dark' : 'theme-light'}
                                  `}
                                  title={t('admin.coming_soon')}
                                >
                                  <div className="flex-shrink-0">
                                    <IconComponent iconName={subItem.icon} isActive={false} />
                                  </div>
                                  <span className="font-medium whitespace-nowrap" style={{
                                    color: isDark ? '#d2d6db' : '#4a4a4a'
                                  }}>{subItem.label}</span>
                                  <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                                    style={{
                                      background: isDark ? 'rgba(201,160,56,0.15)' : 'rgba(201,160,56,0.12)',
                                      color: isDark ? '#c9a038' : '#7d6633',
                                      border: isDark ? '1px solid rgba(201,160,56,0.25)' : '1px solid rgba(125,102,51,0.2)',
                                    }}
                                  >{t('admin.soon')}</span>
                                </div>
                              ) : (
                              <NavLink
                                key={subItem.path}
                                to={subItem.path}
                                className={({ isActive }) => `
                                  flex items-center space-x-3 admin-nav-item clickable-item text-sm mx-2 py-2 px-3 rounded-md
                                  ${isActive ? 'active nav-item-active-entrance' : ''}
                                  ${isDark ? 'theme-dark hover:bg-gray-700/50' : 'theme-light hover:bg-white/70'}
                                  transition-colors duration-150
                                `}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Close sidebar on mobile but keep dropdown open
                                  onClose();
                                  // Don't close dropdown on item click - let user manually close it
                                }}
                              >
                                {({ isActive }) => (
                                  <>
                                    <div className="flex-shrink-0">
                                      <IconComponent iconName={subItem.icon} isActive={isActive} />
                                    </div>
                                    <span className="font-medium whitespace-nowrap" style={{
                                      color: isActive 
                                        ? (isDark ? '#f0d9a6' : 'var(--gold-600)')
                                        : (isDark ? '#d2d6db' : '#4a4a4a')
                                    }}>{subItem.label}</span>
                                  </>
                                )}
                              </NavLink>
                              )
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Regular menu item
                    <NavLink
                      to={item.path}
                      end={item.path === '/admin'}
                      className={({ isActive }) => `
                        flex items-center admin-nav-item clickable-item
                        ${isActive ? 'active nav-item-active-entrance' : ''}
                        ${isDark ? 'theme-dark' : 'theme-light'}
                        ${isCollapsed ? 'justify-center p-2 rounded-lg' : 'space-x-3'}
                      `}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                      }}
                      title={isCollapsed ? item.label : ''}
                    >
                      {({ isActive }) => (
                        <>
                          <div className="relative" style={{minWidth: isCollapsed ? 'auto' : 0}}>
                            <IconComponent iconName={item.icon} isActive={isActive} />
                            {item.path === '/admin/inventory' && lowStockCount > 0 && (
                              <span className={`absolute bg-red-500 text-white text-xs rounded-full flex items-center justify-center ${
                                isCollapsed ? '-top-1 -right-1 h-4 w-4' : '-top-1 -right-1 h-5 w-5'
                              }`}>
                                {isCollapsed ? '!' : lowStockCount}
                              </span>
                            )}
                          </div>
                          {!isCollapsed && (
                            <span className="font-medium" style={{
                              color: isActive 
                                ? (isDark ? '#f0d9a6' : 'var(--gold-600)')
                                : (isDark ? 'var(--text)' : 'var(--text)')
                            }}>{item.label}</span>
                          )}
                        </>
                      )}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>

            {/* Mobile + Tablet only: Contact Support & Logout */}
            <div className="min-[1025px]:hidden mt-6 pt-4 border-t space-y-2" style={{ borderColor: isDark ? 'var(--panel-border)' : 'var(--panel-border)' }}>
              <button
                onClick={() => {
                  navigate(shellConfig?.contactPath || '/admin/contact');
                  onClose();
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isDark ? 'text-gray-300 hover:bg-gray-700/50' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8-1.086 0-2.125-.172-3.09-.488L3 20l1.488-5.91C4.172 13.125 4 12.086 4 11c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span className="font-medium">{t('admin.contact_support')}</span>
              </button>

              <button
                onClick={() => {
                  effectiveLogout();
                  navigate(shellConfig?.logoutRedirectPath || '/?auth=1');
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isDark ? 'text-red-400 hover:bg-red-900/20' : 'text-red-600 hover:bg-red-50'
                }`}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="font-medium">{t('admin.logout')}</span>
              </button>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;
